import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import {
  createColaboradorApi,
  fetchColaboradoresOpcoes,
  sugerirUsuarioApi,
  updateColaboradorApi,
  type GgColaboradorDto,
} from "@/services/genteGestao";
import { usuarioBaseFromNome } from "@/lib/gente-gestao/format";

interface Props {
  open: boolean;
  colaborador: GgColaboradorDto | null;
  onClose: () => void;
  onSaved: () => void;
}

export function ModalColaboradorGg({ open, colaborador, onClose, onSaved }: Props) {
  const [nome, setNome] = useState("");
  const [usuario, setUsuario] = useState("");
  const [usuarioTouched, setUsuarioTouched] = useState(false);
  const [setor, setSetor] = useState("");
  const [cargo, setCargo] = useState("");
  const [telefone, setTelefone] = useState("");
  const [email, setEmail] = useState("");
  const [liderId, setLiderId] = useState("");
  const [opcoes, setOpcoes] = useState<Array<{ id: string; nome: string; usuario: string }>>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setNome(colaborador?.nome ?? "");
    setUsuario(colaborador?.usuario ?? "");
    setUsuarioTouched(Boolean(colaborador));
    setSetor(colaborador?.setor ?? "");
    setCargo(colaborador?.cargo ?? "");
    setTelefone(colaborador?.telefone ?? "");
    setEmail(colaborador?.email ?? "");
    setLiderId(colaborador?.liderId ?? "");
    setError(null);
    void fetchColaboradoresOpcoes()
      .then(setOpcoes)
      .catch(() => setOpcoes([]));
  }, [open, colaborador]);

  useEffect(() => {
    if (!open || usuarioTouched || colaborador) return;
    const base = usuarioBaseFromNome(nome);
    if (!base) {
      setUsuario("");
      return;
    }
    const t = window.setTimeout(() => {
      void sugerirUsuarioApi(nome)
        .then(setUsuario)
        .catch(() => setUsuario(base));
    }, 250);
    return () => window.clearTimeout(t);
  }, [nome, open, usuarioTouched, colaborador]);

  async function save() {
    if (!nome.trim()) {
      setError("Informe o nome.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        nome: nome.trim(),
        usuario: usuario.trim(),
        setor: setor.trim() || null,
        cargo: cargo.trim() || null,
        telefone: telefone.trim() || null,
        email: email.trim() || null,
        liderId: liderId || null,
      };
      if (colaborador) await updateColaboradorApi(colaborador.id, { ...payload, usuario: payload.usuario });
      else await createColaboradorApi(payload);
      onSaved();
      onClose();
    } catch (e) {
      const ax = e as { response?: { data?: { error?: string } } };
      setError(ax.response?.data?.error || "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={colaborador ? "Editar colaborador" : "Novo colaborador"}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={() => void save()} loading={saving}>
            Salvar
          </Button>
        </div>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Input label="Nome" value={nome} onChange={(e) => setNome(e.target.value)} />
        </div>
        <Input
          label="Usuário"
          value={usuario}
          onChange={(e) => {
            setUsuarioTouched(true);
            setUsuario(e.target.value);
          }}
          hint="Identificador curto na pesquisa"
        />
        <Input label="Setor" value={setor} onChange={(e) => setSetor(e.target.value)} />
        <Input label="Cargo" value={cargo} onChange={(e) => setCargo(e.target.value)} />
        <Input label="Telefone" value={telefone} onChange={(e) => setTelefone(e.target.value)} hint="55DDDNUMERO" />
        <Input label="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} />
        <div className="sm:col-span-2">
          <SearchableSelect
            label="Líder direto"
            value={liderId}
            onChange={setLiderId}
            placeholder="Sem líder direto"
            options={[
              { value: "", label: "Sem líder direto" },
              ...opcoes
                .filter((o) => o.id !== colaborador?.id)
                .map((o) => ({ value: o.id, label: `${o.nome} (${o.usuario})` })),
            ]}
          />
        </div>
        {error && <p className="sm:col-span-2 text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}
